import { Link } from "wouter";
import { ShieldCheck, Feather, MapPin, ArrowRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Section, PageHeader, Eyebrow } from "@/components/Section";

const PRINCIPLES = [
  {
    icon: ShieldCheck,
    title: "Trust",
    body: "Trust is the only product. Every default — fail-closed verification, per-workspace credentials, append-only audit log — exists to make trust the path of least resistance.",
  },
  {
    icon: Feather,
    title: "Quiet Tech",
    body: "Software that disappears into the work. No dark patterns, no growth-hacked dashboards, no friction added for engagement metrics. The right thing should also be the easiest thing.",
  },
  {
    icon: MapPin,
    title: "AU-first",
    body: "Built in Australia for Australian businesses. Australian data sovereignty, ConnectID alignment and Privacy Act 1988 obligations are first-class — not afterthoughts. Whether you're in property, legal, payroll, accounting or insurance, the AU rules come first.",
  },
];

export default function About() {
  return (
    <>
      <Seo
        title="About"
        description="FortifyID is built in Australia for any Australian business that handles outbound payments or sensitive client details. Our mission: end payment-redirection fraud at the source."
        path="/about"
      />
      <Section>
        <PageHeader
          eyebrow="About"
          title="Built in Australia, for Australian businesses."
          lede="We're a small team of engineers and operators who watched too many businesses absorb the cost of a single fraudulent BSB change — across real estate trust accounts, legal disbursements, payroll runs, accountant refunds and claim payouts. FortifyID exists so that doesn't happen on your watch."
        />
      </Section>
      <Section className="!pt-0">
        <div className="grid md:grid-cols-3 gap-12">
          <div className="md:col-span-2 space-y-6 text-[hsl(var(--brand-slate))] leading-relaxed">
            <p>
              The vast majority of payment-redirection fraud doesn't begin with a sophisticated attack on your systems. It begins with a forwarded email, a swapped PDF, or a phone number that no longer belongs to the person you think you're speaking with.
            </p>
            <p>
              FortifyID closes that gap. Every change request leaves your business on a co-branded link, lands in front of a verified identity, and arrives back as structured data with a complete event log. No more PDFs in inboxes. No more "as discussed" payments to accounts that aren't your client's.
            </p>
            <p>
              We work with principals, partners, payroll leads, claims managers and trust-account auditors across multiple industries to keep what we build aligned with the way Australian businesses actually operate.
            </p>
          </div>
          <div className="space-y-8">
            <div>
              <Eyebrow>Founding team</Eyebrow>
              <p className="mt-2 text-[hsl(var(--brand-slate))] leading-relaxed">
                A small group of engineers and operators based in Australia, building FortifyID alongside principals, partners and trust-account auditors across multiple industries.
              </p>
            </div>
            <div>
              <Eyebrow>Built in</Eyebrow>
              <div className="mt-2 font-semibold text-[hsl(var(--brand-charcoal))]">Australia</div>
            </div>
          </div>
        </div>
      </Section>

      {/* Founding principles */}
      <Section className="!pt-0">
        <div className="border-t border-[hsl(var(--brand-sand))] pt-12">
          <Eyebrow>Founding principles</Eyebrow>
          <h2 className="mt-3 text-3xl sm:text-4xl font-semibold text-[hsl(var(--brand-charcoal))] max-w-2xl">
            Three principles we won't trade away.
          </h2>
          <div className="mt-10 grid md:grid-cols-3 gap-px bg-[hsl(var(--brand-sand))] border border-[hsl(var(--brand-sand))] rounded-xl overflow-hidden">
            {PRINCIPLES.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.title} className="bg-[hsl(var(--brand-bone))] p-6 sm:p-8">
                  <div className="w-10 h-10 rounded bg-[hsl(var(--brand-forest))] text-[hsl(var(--brand-brass))] flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="mt-5 text-xl font-semibold text-[hsl(var(--brand-charcoal))]">{p.title}</h3>
                  <p className="mt-2 text-[hsl(var(--brand-slate))] leading-relaxed">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* Hiring */}
      <Section className="!pt-0">
        <div className="rounded-2xl border border-[hsl(var(--brand-sand))] bg-white/50 p-6 sm:p-10 grid md:grid-cols-3 gap-8 items-center">
          <div className="md:col-span-2">
            <Eyebrow>Working with us</Eyebrow>
            <h3 className="mt-3 text-2xl font-semibold text-[hsl(var(--brand-charcoal))]">
              Want to build the safest change-request flow in AU?
            </h3>
            <p className="mt-3 text-[hsl(var(--brand-slate))] leading-relaxed">
              We're not actively hiring yet. If you'd like to be considered when we open roles for engineers or operators in Australia, get in touch via our contact form and we'll keep your details on file.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <Link
              href="/contact?topic=other"
              className="inline-flex items-center justify-center gap-2 bg-[hsl(var(--brand-forest))] text-[hsl(var(--brand-bone))] px-6 py-3 rounded font-medium hover:bg-[hsl(168,25%,22%)] transition-colors"
              data-testid="link-hiring"
            >
              Get in touch <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </Section>
    </>
  );
}
